const MAX_PX = 1500
const JPEG_QUALITY = 0.85

/**
 * Shrink a photo so its longest side is at most 1500px before upload.
 * Phone cameras produce 3–12MB JPEGs; Gemini and the job-order gallery need far less.
 * Non-images (PDF) are returned untouched.
 */
export async function resizeImage(file, maxPx = MAX_PX) {
  if (!/^image\//.test(file.type)) return file
  let bitmap
  try {
    // from-image applies the EXIF orientation so portrait shots don't come out sideways
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    bitmap = await loadViaImg(file)
  }
  const { width, height } = bitmap
  if (width <= maxPx && height <= maxPx && file.size < 1024 * 1024) {
    bitmap.close?.()
    return file
  }
  const scale = Math.min(1, maxPx / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close?.()
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
  return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })
}

function loadViaImg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e) }
    img.src = url
  })
}
